import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Users, Briefcase, Package, FileText, Phone, Mail, Calendar, Clock, CheckCircle, Zap, ExternalLink, X, MessageSquare, Car } from 'lucide-react';
import SubscriptionCard from './SubscriptionCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TenantHome = ({ getAuthHeaders }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [driverStats, setDriverStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [allCallbacks, setAllCallbacks] = useState([]); // Store ALL callbacks
  const [callbacks, setCallbacks] = useState([]); // Store filtered callbacks for display
  const [callbackFilter, setCallbackFilter] = useState('today');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'next_callback_date', direction: 'asc' });
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);
  const [selectedCallback, setSelectedCallback] = useState(null);
  const [callbackDetails, setCallbackDetails] = useState({
    customer: null,
    recentComms: [],
    activeQuotes: [],
    upcomingJobs: [],
    vehicles: []
  });
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const [newCallbackDate, setNewCallbackDate] = useState('');
  const [isLogCallMode, setIsLogCallMode] = useState(false);
  const [callLogNotes, setCallLogNotes] = useState('');
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  // Detail popup states
  const [showQuotesDetail, setShowQuotesDetail] = useState(false);
  const [showJobsDetail, setShowJobsDetail] = useState(false);
  const [showVehiclesDetail, setShowVehiclesDetail] = useState(false);



  useEffect(() => {
    fetchStats();
    fetchCallbacks();
    fetchDriverStats();
  }, []);

  // Filter callbacks when filter or date range changes
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toISOString().split('T')[0];
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowString = tomorrow.toISOString().split('T')[0];
    
    const filtered = allCallbacks.filter(callback => {
      const callbackDate = callback.next_callback_date?.split('T')[0];
      
      if (!callbackDate) return false;
      
      if (callbackFilter === 'today') {
        return callbackDate === todayString;
      } else if (callbackFilter === 'tomorrow') {
        return callbackDate === tomorrowString;
      } else if (callbackFilter === 'past_due') {
        return callbackDate < todayString;
      } else if (callbackFilter === 'custom') {
        if (!customDateRange.start || !customDateRange.end) return false;
        return callbackDate >= customDateRange.start && callbackDate <= customDateRange.end;
      }
      
      return false;
    });
    
    setCallbacks(filtered);
  }, [callbackFilter, customDateRange, allCallbacks]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDriverStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/driver-stats`, getAuthHeaders());
      setDriverStats(response.data);
    } catch (error) {
      console.error('Error fetching driver stats:', error);
      // Set default empty stats if error
      setDriverStats({
        driver_module_enabled: false,
        stats: {}
      });
    }
  };

  // Helper function to calculate callback counts for each filter
  const getCallbackCounts = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toISOString().split('T')[0];
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowString = tomorrow.toISOString().split('T')[0];
    
    const counts = {
      today: 0,
      tomorrow: 0,
      pastDue: 0,
      total: allCallbacks.length
    };
    
    allCallbacks.forEach(callback => {
      const callbackDate = callback.next_callback_date?.split('T')[0];
      if (!callbackDate) return;
      
      if (callbackDate === todayString) {
        counts.today++;
      } else if (callbackDate === tomorrowString) {
        counts.tomorrow++;
      } else if (callbackDate < todayString) {
        counts.pastDue++;
      }
    });
    
    return counts;
  };

  const callbackCounts = getCallbackCounts();

  const fetchCallbacks = async () => {
    try {
      // Fetch all communication logs with callbacks
      const response = await axios.get(`${API}/communication_logs`, getAuthHeaders());
      const logs = response.data;
      
      // Get only logs that have next_callback_date
      const logsWithCallbacks = logs.filter(log => log.next_callback_date);
      
      // Fetch customer details for each callback
      const callbacksWithDetails = await Promise.all(
        logsWithCallbacks.map(async (log) => {
          try {
            const customerResponse = await axios.get(`${API}/customers/${log.customer_id}`, getAuthHeaders());
            return {
              ...log,
              company_name: customerResponse.data.name,
              phone_number: log.contact_phone || customerResponse.data.phone || 'N/A',
              email_address: log.contact_email || customerResponse.data.email || 'N/A'
            };
          } catch (error) {
            console.error('Error fetching customer:', error);
            return {
              ...log,
              company_name: 'Unknown',
              phone_number: 'N/A',
              email_address: 'N/A'
            };
          }
        })
      );
      
      // Store all callbacks
      setAllCallbacks(callbacksWithDetails);
      
      // Filter will be applied separately via useMemo or useEffect
    } catch (error) {
      console.error('Error fetching callbacks:', error);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCallbacks = [...callbacks].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    }
    return 0;
  });

  const handleCallbackClick = async (callback) => {
    setSelectedCallback(callback);
    setIsRescheduleMode(false);
    setIsLogCallMode(false);
    setIsLoadingDetails(true);
    
    try {
      // Fetch ALL data BEFORE opening modal
      const customerResponse = await axios.get(`${API}/customers/${callback.customer_id}`, getAuthHeaders());
      
      const commsResponse = await axios.get(`${API}/customers/${callback.customer_id}/communication_logs`, getAuthHeaders());
      const recentComms = commsResponse.data.slice(0, 5);
      
      let activeQuotes = [];
      try {
        const quotesResponse = await axios.get(`${API}/quotes?customer_id=${callback.customer_id}`, getAuthHeaders());
        activeQuotes = quotesResponse.data.filter(q => q.status === 'pending' || q.status === 'sent');
      } catch (error) {
        console.error('Error fetching quotes:', error);
      }
      
      let upcomingJobs = [];
      try {
        const jobsResponse = await axios.get(`${API}/jobs?customer_id=${callback.customer_id}`, getAuthHeaders());
        const today = new Date();
        const todayDateString = today.toISOString().split('T')[0];
        upcomingJobs = jobsResponse.data.filter(j => {
          // Use scheduled_start field (not scheduled_date) and include jobs with future dates
          const jobDateString = j.scheduled_start ? j.scheduled_start.split('T')[0] : '';
          return jobDateString >= todayDateString;
        });
        console.log('Filtered upcoming jobs:', upcomingJobs.length);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
      
      let vehicles = [];
      try {
        const vehiclesResponse = await axios.get(`${API}/customers/${callback.customer_id}/vehicles`, getAuthHeaders());
        vehicles = vehiclesResponse.data;
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      }
      
      // Set all data at once
      const newDetails = {
        customer: customerResponse.data,
        recentComms,
        activeQuotes,
        upcomingJobs,
        vehicles
      };
      
      console.log('About to set details and open modal with:', {
        activeQuotes: activeQuotes.length,
        upcomingJobs: upcomingJobs.length,
        vehicles: vehicles.length
      });
      
      setCallbackDetails(newDetails);
      setIsLoadingDetails(false);
      
      // Open modal AFTER all data is ready
      setIsCallbackModalOpen(true);
    } catch (error) {
      console.error('Error fetching callback details:', error);
      alert('Failed to load callback details');
      setIsLoadingDetails(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!window.confirm('Mark this callback as complete? This will remove it from the callbacks list.')) {
      return;
    }
    
    try {
      // Remove the next_callback_date from the communication log
      await axios.put(
        `${API}/communication_logs/${selectedCallback.id}`,
        { next_callback_date: null },
        getAuthHeaders()
      );
      alert('✓ Callback marked as complete and removed from the list');
      setIsCallbackModalOpen(false);
      await fetchCallbacks(); // Refresh the list
    } catch (error) {
      console.error('Error marking callback complete:', error);
      alert('Failed to mark callback complete');
    }
  };

  const handleReschedule = async () => {
    if (!newCallbackDate) {
      alert('Please select a new callback date');
      return;
    }
    
    try {
      await axios.put(
        `${API}/communication_logs/${selectedCallback.id}`,
        { next_callback_date: newCallbackDate },
        getAuthHeaders()
      );
      alert('Callback rescheduled');
      setIsCallbackModalOpen(false);
      setIsRescheduleMode(false);
      await fetchCallbacks();
    } catch (error) {
      console.error('Error rescheduling callback:', error);
      alert('Failed to reschedule callback');
    }
  };

  const handleLogCallResult = async () => {
    if (!callLogNotes.trim()) {
      alert('Please enter call notes');
      return;
    }
    
    try {
      const now = new Date();
      const dateTimeLocal = now.toISOString().slice(0, 16);
      
      await axios.post(
        `${API}/communication_logs`,
        {
          customer_id: selectedCallback.customer_id,
          contact_name: selectedCallback.contact_name,
          date: dateTimeLocal,
          notes: callLogNotes,
          communication_method: 'phone',
          contact_made: true,
          next_callback_date: null
        },
        getAuthHeaders()
      );
      
      alert('Call logged successfully');
      setIsLogCallMode(false);
      setCallLogNotes('');
      
      // Refresh recent comms in the modal
      const commsResponse = await axios.get(`${API}/customers/${selectedCallback.customer_id}/communication_logs`, getAuthHeaders());
      setCallbackDetails(prev => ({
        ...prev,
        recentComms: commsResponse.data.slice(0, 5)
      }));
    } catch (error) {
      console.error('Error logging call:', error);
      alert('Failed to log call');
    }
  };

  const openQuickQuote = () => {
    // Navigate to customer detail page with quick quote state
    navigate(`/dashboard/customers/${selectedCallback.customer_id}`, { state: { openQuickQuote: true } });
  };


  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  const statCards = [
    { label: 'Total Customers', value: stats?.total_customers || 0, icon: Users, color: 'from-blue-500 to-blue-600', testId: 'total-customers' },
    { label: 'Total Jobs', value: stats?.total_jobs || 0, icon: Briefcase, color: 'from-green-500 to-green-600', testId: 'total-jobs' },
    { label: 'Jobs Today', value: stats?.jobs_today || 0, icon: FileText, color: 'from-purple-500 to-purple-600', testId: 'jobs-today' },
    { label: 'Pending Quotes', value: stats?.pending_quotes || 0, icon: Package, color: 'from-orange-500 to-orange-600', testId: 'pending-quotes' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>Dashboard</h2>
        <p className="text-gray-500">Your business overview</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
                <p className="text-3xl font-bold" data-testid={stat.testId}>{stat.value}</p>
              </div>
              <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Driver Module Widgets */}
      {driverStats?.driver_module_enabled && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>Driver Operations</h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/dashboard/drivers-calendar')}
              className="gap-2"
            >
              <Calendar className="w-4 h-4" />
              View Calendar
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Jobs In Progress</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {driverStats?.stats?.summary?.total_in_progress || 0}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-yellow-100">
                  <Zap className="w-5 h-5 text-yellow-600" />
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Completed Today</p>
                  <p className="text-2xl font-bold text-green-600">
                    {driverStats?.stats?.summary?.total_completed_today || 0}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-green-100">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending This Week</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {driverStats?.stats?.summary?.total_pending_this_week || 0}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-blue-100">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Drivers</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {driverStats?.stats?.total_active_drivers || 0}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-purple-100">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </Card>
          </div>

          {/* Driver Performance Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="p-4">
              <h4 className="font-semibold mb-3">Jobs In Progress by Driver</h4>
              {Object.keys(driverStats?.stats?.jobs_in_progress_by_driver || {}).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(driverStats.stats.jobs_in_progress_by_driver).map(([driver, count]) => (
                    <div key={driver} className="flex justify-between items-center">
                      <span className="text-sm">{driver}</span>
                      <span className="font-semibold text-yellow-600">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No jobs currently in progress</p>
              )}
            </Card>

            <Card className="p-4">
              <h4 className="font-semibold mb-3">Completed Today by Driver</h4>
              {Object.keys(driverStats?.stats?.jobs_completed_today_by_driver || {}).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(driverStats.stats.jobs_completed_today_by_driver).map(([driver, count]) => (
                    <div key={driver} className="flex justify-between items-center">
                      <span className="text-sm">{driver}</span>
                      <span className="font-semibold text-green-600">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No jobs completed today yet</p>
              )}
            </Card>

            <Card className="p-4">
              <h4 className="font-semibold mb-3">Pending This Week by Driver</h4>
              {Object.keys(driverStats?.stats?.pending_jobs_this_week_by_driver || {}).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(driverStats.stats.pending_jobs_this_week_by_driver).map(([driver, count]) => (
                    <div key={driver} className="flex justify-between items-center">
                      <span className="text-sm">{driver}</span>
                      <span className="font-semibold text-blue-600">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No pending jobs this week</p>
              )}
            </Card>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>Quick Stats</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium">Jobs This Week</span>
              <span className="text-lg font-bold text-blue-600" data-testid="jobs-this-week">{stats?.jobs_this_week || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium">Revenue This Month</span>
              <span className="text-lg font-bold text-green-600" data-testid="revenue-this-month">${stats?.revenue_this_month?.toFixed(2) || '0.00'}</span>
            </div>
          </div>
        </Card>

        <SubscriptionCard getAuthHeaders={getAuthHeaders} />
      </div>

      {/* Callbacks Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              <Phone className="w-6 h-6" />
              Scheduled Callbacks
            </h3>
            <p className="text-sm text-gray-500 mt-1">Manage your follow-up calls</p>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant={callbackFilter === 'today' ? 'default' : 'outline'}
            onClick={() => setCallbackFilter('today')}
            className="gap-2"
          >
            <Clock className="w-4 h-4" />
            Today ({callbackCounts.today})
          </Button>
          <Button
            variant={callbackFilter === 'tomorrow' ? 'default' : 'outline'}
            onClick={() => setCallbackFilter('tomorrow')}
            className="gap-2"
          >
            <Calendar className="w-4 h-4" />
            Tomorrow ({callbackCounts.tomorrow})
          </Button>
          <Button
            variant={callbackFilter === 'past_due' ? 'default' : 'outline'}
            onClick={() => setCallbackFilter('past_due')}
            className="gap-2"
          >
            <Clock className="w-4 h-4 text-red-500" />
            Past Due ({callbackCounts.pastDue})
          </Button>
          <Button
            variant={callbackFilter === 'custom' ? 'default' : 'outline'}
            onClick={() => setCallbackFilter('custom')}
          >
            Custom Range
          </Button>
        </div>

        {/* Custom Date Range Inputs */}
        {callbackFilter === 'custom' && (
          <div className="flex gap-4 mb-6 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block">Start Date</label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block">End Date</label>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>
        )}

        {/* Callbacks Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="w-[130px] cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('next_callback_date')}
                >
                  Callback Date {sortConfig.key === 'next_callback_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead 
                  className="w-[150px] cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('contact_name')}
                >
                  Contact {sortConfig.key === 'contact_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead 
                  className="w-[180px] cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('company_name')}
                >
                  Company Name {sortConfig.key === 'company_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead className="w-[140px]">Phone Number</TableHead>
                <TableHead className="w-[200px]">Email Address</TableHead>
                <TableHead className="w-auto">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCallbacks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                    <Phone className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No callbacks scheduled for this period</p>
                    <p className="text-sm">Callbacks will appear here when you set "Next Call Back Date" in customer communication logs</p>
                  </TableCell>
                </TableRow>
              ) : (
                sortedCallbacks.map((callback) => (
                  <TableRow 
                    key={callback.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleCallbackClick(callback)}
                  >
                    <TableCell className="font-medium">
                      {new Date(callback.next_callback_date).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell>{callback.contact_name}</TableCell>
                    <TableCell>{callback.company_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        {callback.phone_number}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {callback.email_address}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-md">
                      <div 
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {callback.notes}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Callback Detail Modal */}
      <Dialog open={isCallbackModalOpen} onOpenChange={setIsCallbackModalOpen} key={selectedCallback?.id}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Phone className="w-6 h-6" />
              Callback Details
            </DialogTitle>
          </DialogHeader>

          {callbackDetails.customer && (
            <div className="space-y-6">
              {/* Customer Overview */}
              <Card className="p-4 bg-blue-50">
                <h3 className="font-bold text-lg mb-3">Customer Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Company/Name</p>
                    <p className="font-medium">{callbackDetails.customer.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Type</p>
                    <p className="font-medium capitalize">{callbackDetails.customer.customer_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {callbackDetails.customer.phone || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {callbackDetails.customer.email || 'N/A'}
                    </p>
                  </div>
                  {callbackDetails.customer.address_1 && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-600">Address</p>
                      <p className="font-medium">
                        {[callbackDetails.customer.address_1, callbackDetails.customer.city, callbackDetails.customer.postcode].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </Card>

              {/* This Callback Info */}
              <Card className="p-4 bg-yellow-50">
                <h3 className="font-bold text-lg mb-3">This Callback</h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-600">Scheduled Callback Date</p>
                    <p className="font-medium text-lg">
                      {new Date(selectedCallback.next_callback_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Contact Person</p>
                    <p className="font-medium">{selectedCallback.contact_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Original Communication Date</p>
                    <p className="font-medium">{new Date(selectedCallback.date).toLocaleDateString('en-GB')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Original Notes</p>
                    <div 
                      className="font-medium bg-white p-3 rounded border max-h-[200px] overflow-y-auto overflow-x-hidden whitespace-pre-wrap"
                      style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                    >
                      {selectedCallback.notes}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="default" 
                  className="w-full gap-2 bg-green-600 hover:bg-green-700"
                  onClick={handleMarkComplete}
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark Complete
                </Button>
                <Button 
                  variant="default" 
                  className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                  onClick={openQuickQuote}
                >
                  <Zap className="w-4 h-4" />
                  Quick Quote
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full gap-2"
                  onClick={() => setIsRescheduleMode(!isRescheduleMode)}
                >
                  <Calendar className="w-4 h-4" />
                  Reschedule
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full gap-2"
                  onClick={() => setIsLogCallMode(!isLogCallMode)}
                >
                  <MessageSquare className="w-4 h-4" />
                  Log Call Result
                </Button>
                <Button 
                  variant="outline" 
                  className="col-span-2 gap-2"
                  onClick={() => navigate(`/dashboard/customers/${selectedCallback.customer_id}`)}
                >
                  <ExternalLink className="w-4 h-4" />
                  View Full Customer Record
                </Button>
              </div>

              {/* Reschedule Mode */}
              {isRescheduleMode && (
                <Card className="p-4 bg-orange-50">
                  <h4 className="font-bold mb-2">Reschedule Callback</h4>
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={newCallbackDate}
                      onChange={(e) => setNewCallbackDate(e.target.value)}
                      className="flex-1"
                    />
                    <Button onClick={handleReschedule}>Save</Button>
                    <Button variant="outline" onClick={() => setIsRescheduleMode(false)}>Cancel</Button>
                  </div>
                </Card>
              )}

              {/* Log Call Mode */}
              {isLogCallMode && (
                <Card className="p-4 bg-green-50">
                  <h4 className="font-bold mb-2">Log Call Result</h4>
                  <div className="space-y-2">
                    <textarea
                      value={callLogNotes}
                      onChange={(e) => setCallLogNotes(e.target.value)}
                      placeholder="Enter call notes..."
                      className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleLogCallResult}>Save Call Log</Button>
                      <Button variant="outline" onClick={() => setIsLogCallMode(false)}>Cancel</Button>
                    </div>
                  </div>
                </Card>
              )}

              {/* Recent Communications */}
              <Card className="p-4">
                <h3 className="font-bold text-lg mb-3">Recent Communications</h3>
                {callbackDetails.recentComms.length === 0 ? (
                  <p className="text-gray-500">No recent communications</p>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                    {callbackDetails.recentComms.map((comm) => (
                      <div key={comm.id} className="border-l-4 border-blue-500 pl-3 py-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{comm.contact_name}</p>
                            <p className="text-sm text-gray-600">
                              {new Date(comm.date).toLocaleDateString('en-GB')} - {comm.communication_method}
                            </p>
                          </div>
                        </div>
                        <div 
                          className="text-sm mt-1 max-h-[100px] overflow-y-auto overflow-x-hidden whitespace-pre-wrap"
                          style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                        >
                          {comm.notes}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Additional Info Grid */}
              <div className="grid grid-cols-3 gap-4">
                {/* Active Quotes */}
                <Card 
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => callbackDetails.activeQuotes.length > 0 && setShowQuotesDetail(true)}
                >
                  <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Active Quotes
                  </h4>
                  <p className="text-2xl font-bold">
                    {isLoadingDetails ? '...' : callbackDetails.activeQuotes.length}
                  </p>
                  {callbackDetails.activeQuotes.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">Click to view details</p>
                  )}
                </Card>

                {/* Upcoming Jobs */}
                <Card 
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => callbackDetails.upcomingJobs.length > 0 && setShowJobsDetail(true)}
                >
                  <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Upcoming Jobs
                  </h4>
                  <p className="text-2xl font-bold">
                    {isLoadingDetails ? '...' : String(callbackDetails.upcomingJobs.length)}
                  </p>
                  {callbackDetails.upcomingJobs.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">Click to view details</p>
                  )}
                </Card>

                {/* Vehicles */}
                <Card 
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => callbackDetails.vehicles.length > 0 && setShowVehiclesDetail(true)}
                >
                  <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                    <Car className="w-4 h-4" />
                    Vehicles
                  </h4>
                  <p className="text-2xl font-bold">
                    {isLoadingDetails ? '...' : callbackDetails.vehicles.length}
                  </p>
                  {callbackDetails.vehicles.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">Click to view details</p>
                  )}
                </Card>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quotes Detail Dialog */}
      <Dialog open={showQuotesDetail} onOpenChange={setShowQuotesDetail}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Active Quotes - {callbackDetails.customer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {callbackDetails.activeQuotes.map((quote) => (
              <Card key={quote.id} className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-bold text-lg">{quote.quote_number}</p>
                    <p className="text-sm text-gray-600">
                      Status: <span className="capitalize">{quote.status}</span>
                    </p>
                    {quote.contact_name && (
                      <p className="text-sm text-gray-600">Contact: {quote.contact_name}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-xl">£{quote.total?.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">
                      Created: {new Date(quote.created_at).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                </div>
                {quote.items && quote.items.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm font-semibold mb-2">Items:</p>
                    {quote.items.map((item, idx) => (
                      <p key={idx} className="text-sm text-gray-700">
                        • {item.description} - £{item.unit_price?.toFixed(2)} x {item.quantity}
                      </p>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Jobs Detail Dialog */}
      <Dialog open={showJobsDetail} onOpenChange={setShowJobsDetail}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Upcoming Jobs - {callbackDetails.customer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {callbackDetails.upcomingJobs.map((job) => (
              <Card key={job.id} className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-bold text-lg">{job.job_number}</p>
                    <p className="text-sm text-gray-600">
                      Status: <span className="capitalize">{job.status}</span>
                    </p>
                    {job.scheduled_start && (
                      <p className="text-sm text-gray-600">
                        Scheduled: {new Date(job.scheduled_start).toLocaleDateString('en-GB')} at{' '}
                        {new Date(job.scheduled_start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                    {job.scheduled_location && (
                      <p className="text-sm text-gray-600">Location: {job.scheduled_location}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-xl">£{job.total?.toFixed(2)}</p>
                    {job.location_type && (
                      <p className="text-xs text-gray-500 capitalize">{job.location_type}</p>
                    )}
                  </div>
                </div>
                {job.items && job.items.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm font-semibold mb-2">Services:</p>
                    {job.items.map((item, idx) => (
                      <p key={idx} className="text-sm text-gray-700">
                        • {item.description} - £{item.unit_price?.toFixed(2)} x {item.quantity}
                      </p>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Vehicles Detail Dialog */}
      <Dialog open={showVehiclesDetail} onOpenChange={setShowVehiclesDetail}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5" />
              Vehicles - {callbackDetails.customer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {callbackDetails.vehicles.map((vehicle) => (
              <Card key={vehicle.id} className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="font-bold text-lg">{vehicle.registration_number}</p>
                    <p className="text-gray-700">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </p>
                    {vehicle.color && (
                      <p className="text-sm text-gray-600">Color: {vehicle.color}</p>
                    )}
                    {vehicle.vehicle_type && (
                      <p className="text-sm text-gray-600 capitalize">Type: {vehicle.vehicle_type}</p>
                    )}
                  </div>
                  <div className="text-right">
                    {vehicle.fuel_type && (
                      <p className="text-sm text-gray-600 capitalize">Fuel: {vehicle.fuel_type}</p>
                    )}
                    {vehicle.mot_expiry_date && (
                      <p className="text-sm text-gray-600">
                        MOT Expiry: {new Date(vehicle.mot_expiry_date).toLocaleDateString('en-GB')}
                      </p>
                    )}
                    {vehicle.mileage && (
                      <p className="text-sm text-gray-600">Mileage: {vehicle.mileage?.toLocaleString()} miles</p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantHome;
